import type { LucideIcon } from "lucide-react";
import { AlertTriangle, SearchX } from "lucide-react";

import type {
  CarrierAddOnServiceType,
  CarrierProviderAddOnService,
} from "@/api/carrier";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { RowMenu, RowMenuItem } from "@/components/ui/row-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { hasHttpStatus } from "@/lib/api-error";
import { formatAddOnDetailSummary } from "@/lib/add-on-service-format";

export interface AddOnTypeConfig {
  type: CarrierAddOnServiceType;
  title: string;
  shortLabel: string;
  description: string;
  emptyTitle: string;
  emptyDescription: string;
  Icon: LucideIcon;
}

export interface AddOnProviderGroup {
  providerId: string;
  name: string;
  services: CarrierProviderAddOnService[];
}

interface AddOnServiceCatalogueProps {
  config: AddOnTypeConfig;
  groups: AddOnProviderGroup[];
  totalCount: number;
  currentGroupId?: string;
  search: string;
  isLoading: boolean;
  error: unknown;
  mutationTarget: { id: string; action: "rename" | "toggle" } | null;
  mutationError: string | null;
  onRetry: () => void;
  onGroupChange: (providerId: string) => void;
  onView: (service: CarrierProviderAddOnService) => void;
  onRename: (service: CarrierProviderAddOnService) => void;
  onToggle: (service: CarrierProviderAddOnService) => void;
}

export function AddOnServiceCatalogue({
  config,
  groups,
  totalCount,
  currentGroupId,
  search,
  isLoading,
  error,
  mutationTarget,
  mutationError,
  onRetry,
  onGroupChange,
  onView,
  onRename,
  onToggle,
}: AddOnServiceCatalogueProps) {
  if (isLoading) return <AddOnLoadingState />;
  if (error) {
    const forbidden = hasHttpStatus(error, 403);
    return (
      <div className="p-6 text-center text-muted-foreground" role="alert">
        <AlertTriangle className="mx-auto mb-2 h-8 w-8 text-destructive" />
        <p>
          {forbidden
            ? `You do not have permission to view ${config.shortLabel.toLowerCase()}.`
            : `Couldn’t load ${config.shortLabel.toLowerCase()}.`}
        </p>
        {!forbidden && (
          <Button
            variant="outline"
            size="touch"
            className="mt-3"
            onClick={onRetry}
          >
            Try again
          </Button>
        )}
      </div>
    );
  }
  if (totalCount === 0) return <AddOnEmptyState config={config} />;
  if (groups.length === 0) {
    return (
      <div className="p-6 text-center text-muted-foreground" role="status">
        <SearchX className="mx-auto mb-2 h-8 w-8" />
        <p className="font-medium text-foreground">No matching services</p>
        <p className="mt-1 text-sm">
          No {config.shortLabel.toLowerCase()} match “{search}”.
        </p>
      </div>
    );
  }

  const activeGroup =
    groups.find((group) => group.providerId === currentGroupId) ?? groups[0];

  return (
    <div className="space-y-3">
      <ProviderTabs
        groups={groups}
        currentGroupId={activeGroup?.providerId}
        onGroupChange={onGroupChange}
      />
      {mutationError && (
        <p
          role="alert"
          className="rounded-md border bg-destructive/5 p-3 text-sm text-destructive"
        >
          {mutationError}
        </p>
      )}
      {activeGroup && (
        <ServiceRows
          services={activeGroup.services}
          mutationTarget={mutationTarget}
          onView={onView}
          onRename={onRename}
          onToggle={onToggle}
        />
      )}
    </div>
  );
}

function ProviderTabs({
  groups,
  currentGroupId,
  onGroupChange,
}: {
  groups: AddOnProviderGroup[];
  currentGroupId?: string;
  onGroupChange: (providerId: string) => void;
}) {
  return (
    <Tabs value={currentGroupId} onValueChange={onGroupChange}>
      <div className="max-w-full overflow-x-auto pb-1">
        <TabsList size="touch" className="min-w-max justify-start">
          {groups.map((group) => (
            <TabsTrigger
              key={group.providerId}
              value={group.providerId}
              className="px-3"
            >
              {group.name}
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-[11px]">
                {group.services.length}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
      </div>
    </Tabs>
  );
}

function ServiceRows({
  services,
  mutationTarget,
  onView,
  onRename,
  onToggle,
}: {
  services: CarrierProviderAddOnService[];
  mutationTarget: { id: string; action: "rename" | "toggle" } | null;
  onView: (service: CarrierProviderAddOnService) => void;
  onRename: (service: CarrierProviderAddOnService) => void;
  onToggle: (service: CarrierProviderAddOnService) => void;
}) {
  return (
    <>
      <div className="space-y-3 md:hidden">
        {services.map((service) => {
          const pending = mutationTarget?.id === service.id;
          const mutationsBlocked = mutationTarget !== null;
          return (
            <article
              key={service.id}
              className="space-y-3 rounded-md border p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-medium">
                    {service.name}
                  </h3>
                  <p className="break-all font-mono text-xs text-muted-foreground">
                    {service.code}
                  </p>
                </div>
                <StatusBadge status={service.active ? "ACTIVE" : "INACTIVE"} />
              </div>
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-xs text-muted-foreground">
                    Configuration
                  </dt>
                  <dd className="mt-0.5 text-xs leading-5">
                    {formatAddOnDetailSummary(service, 5)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">
                    Enabled label carriers
                  </dt>
                  <dd className="mt-0.5 text-xs">
                    {formatEnabledLabelCarriers(service)}
                  </dd>
                </div>
              </dl>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant="outline"
                  size="touch"
                  className="px-2"
                  onClick={() => onView(service)}
                >
                  View
                </Button>
                <Button
                  variant="outline"
                  size="touch"
                  className="px-2"
                  disabled={mutationsBlocked}
                  onClick={() => onRename(service)}
                >
                  Rename
                </Button>
                <Button
                  variant="outline"
                  size="touch"
                  className="px-2"
                  disabled={mutationsBlocked}
                  onClick={() => onToggle(service)}
                >
                  {pending && mutationTarget?.action === "toggle"
                    ? "Saving…"
                    : service.active
                      ? "Disable"
                      : "Enable"}
                </Button>
              </div>
            </article>
          );
        })}
      </div>

      <div className="hidden overflow-x-auto rounded-md border md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Service</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Configuration</TableHead>
              <TableHead>Enabled label carriers</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {services.map((service) => {
              const pending = mutationTarget?.id === service.id;
              return (
                <TableRow key={service.id} aria-busy={pending || undefined}>
                  <TableCell>
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto justify-start p-0 text-left"
                      onClick={() => onView(service)}
                    >
                      <span className="block font-medium">{service.name}</span>
                      <span className="block font-mono text-xs text-muted-foreground">
                        {service.code}
                      </span>
                    </Button>
                  </TableCell>
                  <TableCell>
                    <StatusBadge
                      status={service.active ? "ACTIVE" : "INACTIVE"}
                    />
                  </TableCell>
                  <TableCell className="max-w-md text-xs leading-5">
                    {formatAddOnDetailSummary(service, 5)}
                  </TableCell>
                  <TableCell className="max-w-56 text-xs text-muted-foreground">
                    {formatEnabledLabelCarriers(service)}
                  </TableCell>
                  <TableCell>
                    {pending && (
                      <span role="status" className="sr-only">
                        Saving changes for {service.name}…
                      </span>
                    )}
                    <RowMenu
                      label={`Actions for ${service.name}`}
                      disabled={mutationTarget !== null}
                    >
                      <RowMenuItem onSelect={() => onView(service)}>
                        View details
                      </RowMenuItem>
                      <RowMenuItem onSelect={() => onRename(service)}>
                        Rename
                      </RowMenuItem>
                      <RowMenuItem onSelect={() => onToggle(service)}>
                        {service.active ? "Disable" : "Enable"}
                      </RowMenuItem>
                    </RowMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

function AddOnLoadingState() {
  return (
    <div className="space-y-3 p-3" role="status">
      <span className="sr-only">Loading add-on services…</span>
      <Skeleton className="h-11 w-72 max-w-full" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}

function AddOnEmptyState({ config }: { config: AddOnTypeConfig }) {
  const Icon = config.Icon;
  return (
    <div className="p-6 text-center text-muted-foreground" role="status">
      <Icon className="mx-auto mb-2 h-8 w-8 opacity-60" />
      <p className="font-medium text-foreground">{config.emptyTitle}</p>
      <p className="mt-1 text-sm">{config.emptyDescription}</p>
    </div>
  );
}

function formatEnabledLabelCarriers(
  service: CarrierProviderAddOnService
): string {
  const enabled = service.labelCarrierAccess
    .filter((access) => access.enabled)
    .map((access) => access.labelCarrier.name);
  return enabled.length > 0 ? enabled.join(", ") : "No enabled label carriers";
}
